// src/components/Departments/Departments.tsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Tabs,
  Tab,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import BusinessIcon from "@mui/icons-material/Business";
import FolderIcon from "@mui/icons-material/Folder";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SortIcon from "@mui/icons-material/Sort";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";

import { baseUrl } from "../../constants";
import { getUserInfoFromToken } from "../../utils/Main";
import { handleDateWithTime } from "../../utils/date-handlers";

type DeptOverview = {
  id: number;
  name: string;
  workspaceId: number;
  projectCount: number;
  activeProjectCount: number;
  archivedProjectCount: number;
  completedProjectCount: number;
  taskTotal: number;
  taskDone: number;
  taskOverdue: number;
  progress: number;
};

type ProjectWithStats = {
  id: number;
  name: string;
  description?: string | null;
  status?: string | null;
  priority?: string | null;
  tags?: string[] | null;
  archived?: boolean;
  defaultVisibility?: string;
  startDate?: string | null;
  deadline?: string | null;
  taskTotal: number;
  taskDone: number;
  taskOverdue: number;
  byStatus: Record<string, number>;
};

type DepartmentProjectsResponse = {
  department: { id: number; name: string };
  projects: ProjectWithStats[];
};

type SortKey =
  | "name"
  | "projectsDesc"
  | "projectsAsc"
  | "progressDesc"
  | "overdueDesc";

const tokenHeader = () => {
  const t =
    localStorage.getItem("accessToken") || localStorage.getItem("token");
  return { Authorization: `Bearer ${t}` };
};

/** ===== Manager shapes from backend ===== */
type DeptManager =
  | { id: number; type: "USER"; user: { id: number; username: string; name?: string | null } }
  | { id: number; type: "POSITION"; position: { id: number; name: string } };

/** Positions used by the dialog */
type PositionLite = { id: number; name: string };

/** Members used by the dialog (from GET /departments/:id/members) */
type MemberLite = {
  user: { id: number; username: string; profile?: { name?: string | null } | null };
  position: { id: number; name: string };
};

const Departments: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const user = useMemo(() => getUserInfoFromToken(), []);
  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  const [scope, setScope] = useState<"own" | "all">(
    (isAdmin
      ? (localStorage.getItem("departments_scope") as "own" | "all")
      : "own") || "own"
  );
  useEffect(() => {
    if (isAdmin) localStorage.setItem("departments_scope", scope);
  }, [scope, isAdmin]);

  // overview
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<DeptOverview[]>([]);
  const [qRaw, setQRaw] = useState("");
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("projectsDesc");

  // per-department
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [deptLoading, setDeptLoading] = useState<Record<number, boolean>>({});
  const [deptProjects, setDeptProjects] = useState<
    Record<number, ProjectWithStats[]>
  >({});

  // managers
  const [deptManagers, setDeptManagers] = useState<
    Record<number, DeptManager[]>
  >({});
  const [deptManagersLoading, setDeptManagersLoading] = useState<
    Record<number, boolean>
  >({});

  // assign-managers dialog (positions + users)
  const [mgrDialogOpen, setMgrDialogOpen] = useState(false);
  const [mgrDialogDept, setMgrDialogDept] = useState<DeptOverview | null>(null);

  const [tab, setTab] = useState<0 | 1>(0);

  // positions
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [positions, setPositions] = useState<PositionLite[]>([]);
  const [selectedPositions, setSelectedPositions] = useState<PositionLite[]>(
    []
  );
  const [positionSearch, setPositionSearch] = useState("");

  // users
  const [membersLoading, setMembersLoading] = useState(false);
  const [members, setMembers] = useState<MemberLite[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<
    { id: number; username: string; name?: string | null }[]
  >([]);
  const [userSearch, setUserSearch] = useState("");

  // snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "success" });

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setQ(qRaw.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [qRaw]);

  /** Load overview (and managers in background) */
  const loadOverview = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    try {
      const effScope = isAdmin ? scope : "own";
      const { data } = await axios.get<DeptOverview[]>(
        `${baseUrl}/departments/workspace/overview`,
        {
          headers: tokenHeader(),
          params: { scope: effScope },
          signal: ctrl.signal,
        }
      );
      const arr = Array.isArray(data) ? data : [];
      setItems(arr);
      void fetchManagersForList(arr.map((d) => d.id));
    } catch (e: any) {
      setSnackbar({
        open: true,
        message:
          e?.response?.data?.message || "Failed to load departments overview",
        severity: "error",
      });
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [scope, isAdmin]);

  useEffect(() => {
    loadOverview();
    return () => abortRef.current?.abort();
  }, [loadOverview]);

  /** Fetch managers for multiple depts */
  const fetchManagersForList = async (deptIds: number[]) => {
    const unique = Array.from(new Set(deptIds)).filter(
      (id) => id && !deptManagers[id] && !deptManagersLoading[id]
    );
    if (!unique.length) return;

    setDeptManagersLoading((prev) => {
      const next = { ...prev };
      unique.forEach((id) => (next[id] = true));
      return next;
    });

    try {
      const results = await Promise.allSettled(
        unique.map((id) =>
          axios.get<DeptManager[]>(`${baseUrl}/departments/${id}/managers`, {
            headers: tokenHeader(),
          })
        )
      );
      setDeptManagers((prev) => {
        const next = { ...prev };
        results.forEach((res, idx) => {
          const deptId = unique[idx];
          if (res.status === "fulfilled") {
            next[deptId] = Array.isArray(res.value.data) ? res.value.data : [];
          }
        });
        return next;
      });
    } finally {
      setDeptManagersLoading((prev) => {
        const next = { ...prev };
        unique.forEach((id) => (next[id] = false));
        return next;
      });
    }
  };

  const loadDeptManagers = async (deptId: number) => {
    if (deptManagersLoading[deptId]) return;
    setDeptManagersLoading((p) => ({ ...p, [deptId]: true }));
    try {
      const { data } = await axios.get<DeptManager[]>(
        `${baseUrl}/departments/${deptId}/managers`,
        { headers: tokenHeader() }
      );
      setDeptManagers((p) => ({
        ...p,
        [deptId]: Array.isArray(data) ? data : [],
      }));
    } catch (e: any) {
      setSnackbar({
        open: true,
        message:
          e?.response?.data?.message || "Failed to load department managers",
        severity: "error",
      });
    } finally {
      setDeptManagersLoading((p) => ({ ...p, [deptId]: false }));
    }
  };

  const toggleExpand = async (deptId: number) => {
    const next = !expanded[deptId];
    setExpanded((p) => ({ ...p, [deptId]: next }));
    if (!next) return;

    if (!deptManagers[deptId]) void loadDeptManagers(deptId);

    if (deptProjects[deptId]) return;
    setDeptLoading((p) => ({ ...p, [deptId]: true }));
    try {
      const { data } = await axios.get<DepartmentProjectsResponse>(
        `${baseUrl}/departments/${deptId}/projects`,
        { headers: tokenHeader() }
      );
      setDeptProjects((p) => ({ ...p, [deptId]: data.projects || [] }));
    } catch (e: any) {
      setSnackbar({
        open: true,
        message:
          e?.response?.data?.message || "Failed to load department projects",
        severity: "error",
      });
    } finally {
      setDeptLoading((p) => ({ ...p, [deptId]: false }));
    }
  };

  const filteredSorted = useMemo(() => {
    const base = (items || []).filter((d) =>
      q ? d.name.toLowerCase().includes(q) : true
    );
    const sorted = [...base].sort((a, b) => {
      switch (sortKey) {
        case "name":
          return a.name.localeCompare(b.name);
        case "projectsAsc":
          return a.projectCount - b.projectCount;
        case "projectsDesc":
          return b.projectCount - a.projectCount;
        case "progressDesc":
          return b.progress - a.progress;
        case "overdueDesc":
          return b.taskOverdue - a.taskOverdue;
        default:
          return 0;
      }
    });
    return sorted;
  }, [items, q, sortKey]);

  const exportOverviewExcel = async () => {
    try {
      const rows = filteredSorted.map((d) => ({
        id: d.id,
        name: d.name,
        projectCount: d.projectCount,
        activeProjectCount: d.activeProjectCount,
        archivedProjectCount: d.archivedProjectCount,
        completedProjectCount: d.completedProjectCount,
        taskTotal: d.taskTotal,
        taskDone: d.taskDone,
        taskOverdue: d.taskOverdue,
        progress: `${d.progress}%`,
      }));

      if (!rows.length) {
        setSnackbar({ open: true, message: "No rows to export.", severity: "info" });
        return;
      }

      const mod: any = await import("xlsx");
      const XLSX = mod.default ?? mod;
      const headers = [
        "id","name","projectCount","activeProjectCount","archivedProjectCount",
        "completedProjectCount","taskTotal","taskDone","taskOverdue","progress",
      ] as const;

      const ws = XLSX.utils.json_to_sheet(rows, { header: headers as any });
      (ws as any)["!cols"] = headers.map((h) => {
        const maxLen = Math.max(
          String(h).length,
          ...rows.map((r) => String((r as any)[h] ?? "").length)
        );
        return { wch: Math.min(Math.max(maxLen + 2, 10), 60) };
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Departments");
      XLSX.writeFile(wb, `departments-overview.xlsx`);
      setSnackbar({ open: true, message: "Exported to Excel.", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Excel export failed.", severity: "error" });
    }
  };

  const exportProjectsExcel = async (deptId: number) => {
    try {
      const list = deptProjects[deptId] || [];
      if (!list.length) {
        setSnackbar({ open: true, message: "No projects to export.", severity: "info" });
        return;
      }

      const rows = list.map((p) => ({
        id: p.id,
        name: p.name,
        status: p.status || "",
        priority: p.priority || "",
        startDate: p.startDate ? handleDateWithTime(p.startDate) : "",
        deadline: p.deadline ? handleDateWithTime(p.deadline) : "",
        taskTotal: p.taskTotal,
        taskDone: p.taskDone,
        taskOverdue: p.taskOverdue,
        progress: p.taskTotal ? `${Math.round((p.taskDone / p.taskTotal) * 100)}%` : "0%",
        tags: (p.tags || []).join(" | "),
      }));

      const mod: any = await import("xlsx");
      const XLSX = mod.default ?? mod;
      const headers = [
        "id","name","status","priority","startDate","deadline",
        "taskTotal","taskDone","taskOverdue","progress","tags",
      ] as const;

      const ws = XLSX.utils.json_to_sheet(rows, { header: headers as any });
      (ws as any)["!cols"] = headers.map((h) => {
        const maxLen = Math.max(
          String(h).length,
          ...rows.map((r) => String((r as any)[h] ?? "").length)
        );
        return { wch: Math.min(Math.max(maxLen + 2, 10), 60) };
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Projects");
      const safe = (items.find((i) => i.id === deptId)?.name || `department-${deptId}`).replace(/[\\/:*?"<>|]/g, "_");
      XLSX.writeFile(wb, `${safe}-projects.xlsx`);
      setSnackbar({ open: true, message: "Exported to Excel.", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Excel export failed.", severity: "error" });
    }
  };

  const handleCloseSnackbar = () => setSnackbar((p) => ({ ...p, open: false }));

  /** ====== Assign managers dialog handlers ====== */
  const openAssignManagers = async (dept: DeptOverview) => {
    setMgrDialogDept(dept);
    setMgrDialogOpen(true);
    setTab(0);

    // reset
    setPositionsLoading(true);
    setPositions([]);
    setSelectedPositions([]);
    setPositionSearch("");

    setMembersLoading(true);
    setMembers([]);
    setSelectedUsers([]);
    setUserSearch("");

    try {
      // load positions of department
      const { data: pos } = await axios.get<PositionLite[]>(
        `${baseUrl}/positions/${dept.id}`,
        { headers: tokenHeader() }
      );
      const options = Array.isArray(pos) ? pos : [];
      setPositions(options);

      // load members of department
      const { data: mem } = await axios.get<MemberLite[]>(
        `${baseUrl}/departments/${dept.id}/members`,
        { headers: tokenHeader() }
      );
      const membersList = Array.isArray(mem) ? mem : [];
      setMembers(membersList);

      // ensure managers loaded
      if (!deptManagers[dept.id]) {
        await loadDeptManagers(dept.id);
      }
      const current = deptManagers[dept.id] || [];

      // preselect positions that are already managers
      const managerPositionIds = new Set(
        current.filter((m) => m.type === "POSITION").map((m: any) => m.position.id)
      );
      setSelectedPositions(options.filter((p) => managerPositionIds.has(p.id)));

      // preselect users that are already managers
      const managerUserIds = new Set(
        current.filter((m) => m.type === "USER").map((m: any) => m.user.id)
      );
      const selectedFromMembers = membersList
        .map((m) => ({
          id: m.user.id,
          username: m.user.username,
          name: m.user.profile?.name ?? null,
        }))
        .filter((u) => managerUserIds.has(u.id));
      setSelectedUsers(selectedFromMembers);
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e?.response?.data?.message || "Failed to load managers data",
        severity: "error",
      });
    } finally {
      setPositionsLoading(false);
      setMembersLoading(false);
    }
  };

  const closeAssignManagers = () => {
    setMgrDialogOpen(false);
    setMgrDialogDept(null);
    setPositions([]);
    setSelectedPositions([]);
    setPositionSearch("");
    setMembers([]);
    setSelectedUsers([]);
    setUserSearch("");
  };

  const saveAssignManagers = async () => {
    if (!mgrDialogDept) return;
    const deptId = mgrDialogDept.id;

    const positionIds = Array.from(new Set(selectedPositions.map((p) => p.id)));
    const userIds = Array.from(new Set(selectedUsers.map((u) => u.id)));

    try {
      // Save positions
      await axios.post(
        `${baseUrl}/departments/${deptId}/managers`,
        { positionIds },
        { headers: tokenHeader() }
      );

      // Save users
      await axios.post(
        `${baseUrl}/departments/${deptId}/managers/users`,
        { userIds },
        { headers: tokenHeader() }
      );

      setSnackbar({
        open: true,
        message: "Department managers updated!",
        severity: "success",
      });
      closeAssignManagers();
      await loadDeptManagers(deptId);
    } catch (e: any) {
      setSnackbar({
        open: true,
        message:
          e?.response?.data?.message ||
          "Failed to save department managers",
        severity: "error",
      });
    }
  };

  const removeManager = async (deptId: number, manager: DeptManager) => {
    if (!isAdmin) return;
    try {
      await axios.delete(
        `${baseUrl}/departments/${deptId}/managers/${manager.id}`,
        { headers: tokenHeader() }
      );
      setDeptManagers((prev) => {
        const next = { ...prev };
        next[deptId] = (next[deptId] || []).filter((m) => m.id !== manager.id);
        return next;
      });
      setSnackbar({ open: true, message: "Manager removed", severity: "success" });
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e?.response?.data?.message || "Failed to remove manager",
        severity: "error",
      });
    }
  };

  const renderManagerChips = (deptId: number) => {
    const list = deptManagers[deptId] || [];
    if (deptManagersLoading[deptId] && !list.length) {
      return <Chip size="small" label="Managers…" variant="outlined" />;
    }
    if (!list.length) {
      return <Chip size="small" label="No managers" variant="outlined" />;
    }
    return (
      <>
        {list.map((m) => {
          const key = `${m.type}-${m.id}`;
          const label =
            m.type === "USER"
              ? `@${(m as any).user.username}${
                  (m as any).user.name ? ` (${(m as any).user.name})` : ""
                }`
              : `Pos: ${(m as any).position.name}`;
          const icon =
            m.type === "USER" ? (
              <PersonIcon sx={{ fontSize: 16 }} />
            ) : (
              <WorkIcon sx={{ fontSize: 16 }} />
            );
          return (
            <Chip
              key={key}
              size="small"
              icon={icon}
              label={label}
              variant="outlined"
              onDelete={isAdmin ? () => removeManager(deptId, m) : undefined}
            />
          );
        })}
      </>
    );
  };

  return (
    <Box sx={{ p: 2 }}>

      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        gap={1}
        flexWrap="wrap"
      >
        <Box display="flex" alignItems="center" gap={1.25}>
          <BusinessIcon color="action" />
          <Typography variant="h5" fontWeight={700}>
            Departments
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
          {isAdmin && (
            <TextField
              select
              size="small"
              label="Show"
              value={scope}
              onChange={(e) => setScope(e.target.value as "own" | "all")}
              sx={{ width: 140 }}
            >
              <MenuItem value="own">My departments</MenuItem>
              <MenuItem value="all">All departments</MenuItem>
            </TextField>
          )}

          <TextField
            size="small"
            placeholder="Search department…"
            value={qRaw}
            onChange={(e) => setQRaw(e.target.value)}
            sx={{ width: 260 }}
          />

          <Box display="flex" alignItems="center" gap={1}>
            <SortIcon fontSize="small" />
            <TextField
              select
              size="small"
              label="Sort"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as any)}
              sx={{ width: 220 }}
            >
              <MenuItem value="projectsDesc">Projects: High → Low</MenuItem>
              <MenuItem value="projectsAsc">Projects: Low → High</MenuItem>
              <MenuItem value="progressDesc">Progress: High → Low</MenuItem>
              <MenuItem value="overdueDesc">Overdue: High → Low</MenuItem>
              <MenuItem value="name">Name A → Z</MenuItem>
            </TextField>
          </Box>

          <Tooltip title="Export overview (Excel)">
            <span>
              <IconButton
                onClick={exportOverviewExcel}
                disabled={!filteredSorted.length}
              >
                <FileDownloadIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Refresh">
            <IconButton onClick={loadOverview}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Body */}
      {loading ? (
        <Box sx={{ py: 6, display: "grid", placeItems: "center" }}>
          <CircularProgress />
        </Box>
      ) : !filteredSorted.length ? (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              No departments found.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {filteredSorted.map((d) => {
            const busy = !!deptLoading[d.id];
            const expandedNow = !!expanded[d.id];
            return (
              <Grid item xs={12} key={d.id}>
                <Card>
                  <CardContent sx={{ py: 2 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={2}
                      flexWrap="wrap"
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1.25}
                        flexWrap="wrap"
                      >
                        <BusinessIcon fontSize="small" />
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          sx={{ cursor: "pointer" }}
                          onClick={() => navigate(`/departments/${d.id}`)}
                        >
                          {d.name}
                        </Typography>
                        <Tooltip title="Open details page">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/departments/${d.id}`)}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Chip size="small" label={`${d.projectCount} projects`} />
                        {d.activeProjectCount > 0 && (
                          <Chip
                            size="small"
                            color="info"
                            variant="outlined"
                            label={`${d.activeProjectCount} active`}
                          />
                        )}
                        {d.completedProjectCount > 0 && (
                          <Chip
                            size="small"
                            color="success"
                            variant="outlined"
                            label={`${d.completedProjectCount} done`}
                          />
                        )}
                        {d.archivedProjectCount > 0 && (
                          <Chip
                            size="small"
                            color="default"
                            variant="outlined"
                            label={`${d.archivedProjectCount} archived`}
                          />
                        )}
                        {!!d.taskOverdue && (
                          <Chip size="small" color="error" label={`${d.taskOverdue} overdue`} />
                        )}

                        {/* Manager chips */}
                        <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                          {renderManagerChips(d.id)}
                        </Box>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
                        {/* Admin-only: Assign Managers */}
                        {isAdmin && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ManageAccountsIcon />}
                            onClick={() => openAssignManagers(d)}
                          >
                            Assign Managers
                          </Button>
                        )}

                        <Box sx={{ minWidth: 220 }}>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={0.5}
                          >
                            <Typography variant="caption" color="text.secondary">
                              Progress ({d.taskDone}/{d.taskTotal})
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {d.progress}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={d.progress}
                            sx={{ height: 8, borderRadius: 1 }}
                          />
                        </Box>

                        <Tooltip title="Export this department's projects (Excel)">
                          <span>
                            <IconButton
                              onClick={() => exportProjectsExcel(d.id)}
                              disabled={!deptProjects[d.id]?.length}
                            >
                              <FileDownloadIcon />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Button
                          size="small"
                          endIcon={expandedNow ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          onClick={() => toggleExpand(d.id)}
                        >
                          {expandedNow ? "Hide Projects" : "View Projects"}
                        </Button>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Box>
                      {expandedNow && (
                        <>
                          {busy ? (
                            <Box sx={{ py: 3, display: "grid", placeItems: "center" }}>
                              <CircularProgress />
                            </Box>
                          ) : !deptProjects[d.id]?.length ? (
                            <Typography variant="body2" color="text.secondary">
                              No projects in this department.
                            </Typography>
                          ) : (
                            <List dense>
                              {deptProjects[d.id].map((p) => {
                                const progress = p.taskTotal
                                  ? Math.round((p.taskDone / p.taskTotal) * 100)
                                  : 0;
                                const overdue = p.taskOverdue > 0;
                                return (
                                  <ListItem
                                    key={p.id}
                                    onDoubleClick={() => navigate(`/projects/${p.id}`)}
                                    sx={{
                                      borderRadius: 1,
                                      "&:hover": { bgcolor: theme.palette.action.hover },
                                      cursor: "pointer",
                                      borderLeft: overdue
                                        ? `3px solid ${theme.palette.error.main}`
                                        : "3px solid transparent",
                                      pl: overdue ? 0.5 : 0,
                                    }}
                                    secondaryAction={
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <Box sx={{ width: 120 }}>
                                          <LinearProgress
                                            variant="determinate"
                                            value={progress}
                                            sx={{ height: 6, borderRadius: 1 }}
                                          />
                                        </Box>
                                        {!!p.taskOverdue && (
                                          <Chip size="small" color="error" label={`${p.taskOverdue} overdue`} />
                                        )}
                                      </Box>
                                    }
                                  >
                                    <ListItemText
                                      primary={
                                        <Box
                                          display="flex"
                                          alignItems="center"
                                          gap={1}
                                          flexWrap="wrap"
                                          onClick={() => navigate(`/projects/${p.id}`)}
                                        >
                                          <FolderIcon fontSize="small" />
                                          <Typography variant="body1" fontWeight={600}>
                                            {p.name}
                                          </Typography>
                                          {p.status && <Chip size="small" label={p.status} />}
                                          {p.priority && <Chip size="small" label={p.priority} />}
                                          {p.tags?.length ? (
                                            <Box display="flex" gap={0.5} flexWrap="wrap">
                                              {p.tags.slice(0, 3).map((t, i) => (
                                                <Chip key={`${p.id}-tag-${i}`} size="small" variant="outlined" label={t} />
                                              ))}
                                              {p.tags.length > 3 && (
                                                <Chip size="small" variant="outlined" label={`+${p.tags.length - 3}`} />
                                              )}
                                            </Box>
                                          ) : null}
                                          {p.archived && (
                                            <Chip size="small" color="default" label="Archived" />
                                          )}
                                        </Box>
                                      }
                                      secondary={
                                        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mt={0.5}>
                                          <Typography variant="caption" color="text.secondary">
                                            {p.startDate ? (
                                              <>
                                                <CalendarTodayIcon sx={{ fontSize: 14 }} /> Start:{" "}
                                                {handleDateWithTime(p.startDate)}
                                              </>
                                            ) : (
                                              "Start: —"
                                            )}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {p.deadline ? (
                                              <>
                                                <AccessTimeIcon sx={{ fontSize: 14 }} /> Due:{" "}
                                                {handleDateWithTime(p.deadline)}
                                              </>
                                            ) : (
                                              "Due: —"
                                            )}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Tasks: {p.taskDone}/{p.taskTotal}
                                          </Typography>
                                        </Box>
                                      }
                                    />
                                  </ListItem>
                                );
                              })}
                            </List>
                          )}
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Assign Managers Dialog (admin) */}
      <Dialog open={mgrDialogOpen} onClose={closeAssignManagers} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign Department Managers
          {mgrDialogDept ? ` — ${mgrDialogDept.name}` : ""}
        </DialogTitle>
        <DialogContent>
          <Box mt={1} display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="text.secondary">
              Add managers by <strong>positions</strong> and/or by <strong>users</strong>.
            </Typography>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tab label="Positions" />
              <Tab label="Users" />
            </Tabs>

            {/* Positions tab */}
            {tab === 0 && (
              <Box mt={1.5}>
                <Autocomplete
                  multiple
                  options={positions.filter((p) =>
                    !positionSearch
                      ? true
                      : p.name.toLowerCase().includes(positionSearch.toLowerCase())
                  )}
                  getOptionLabel={(opt) => opt.name}
                  value={selectedPositions}
                  onChange={(_, val) => setSelectedPositions(val)}
                  loading={positionsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Manager positions"
                      placeholder="Search and select positions…"
                      onChange={(e) => setPositionSearch(e.target.value)}
                    />
                  )}
                />
              </Box>
            )}

            {/* Users tab */}
            {tab === 1 && (
              <Box mt={1.5}>
                <Autocomplete
                  multiple
                  options={members
                    .map((m) => ({
                      id: m.user.id,
                      username: m.user.username,
                      name: m.user.profile?.name ?? null,
                    }))
                    .filter((u) =>
                      !userSearch
                        ? true
                        : (u.name || "").toLowerCase().includes(userSearch.toLowerCase()) ||
                          u.username.toLowerCase().includes(userSearch.toLowerCase())
                    )}
                  getOptionLabel={(opt) =>
                    opt.name ? `${opt.name} (@${opt.username})` : `@${opt.username}`
                  }
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  value={selectedUsers}
                  onChange={(_, val) => setSelectedUsers(val)}
                  loading={membersLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Manager users"
                      placeholder="Search and select users…"
                      onChange={(e) => setUserSearch(e.target.value)}
                    />
                  )}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignManagers}>Cancel</Button>
          <Button variant="contained" onClick={saveAssignManagers} disabled={positionsLoading || membersLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Departments;
